import { KeyValueRow } from '@/components/KeyValueRow/KeyValueRow';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { FormSection } from '@/screens/ReportCreation/reportCreation.types';
import { isFieldVisible } from '@/screens/ReportCreation/reportCreation.utils';
import {
  buildDependencyValueMap,
  getConditionalDependencyNames,
} from '@/screens/ReportCreation/utils/conditionalVisibility.utils';
import type { ReactElement } from 'react';
import { SizableText, YStack } from 'tamagui';
import type { DetailFieldLabelOverrides } from '../../reportDetail.types';
import { ReportDetailFieldSource } from './reportDetailFieldRenderer.type';
import {
  formatReportDetailFieldValue,
  getReportDetailFieldValue,
} from './reportDetailFieldRenderer.utils';

export interface ReportDetailFieldRendererProps {
  sections: FormSection[];
  source: ReportDetailFieldSource;
  labelOverrides?: DetailFieldLabelOverrides;
}

const buildDependencyMapForSection = (
  section: ReportDetailFieldRendererProps['sections'][number],
  source: ReportDetailFieldRendererProps['source'],
  language: string,
): Record<string, ReturnType<typeof getReportDetailFieldValue>> => {
  const dependencyNames = getConditionalDependencyNames([section]);
  const dependencyValues = dependencyNames.map((name) =>
    getReportDetailFieldValue(name, source, language),
  );
  return buildDependencyValueMap(dependencyNames, dependencyValues);
};

export const ReportDetailFieldRenderer = ({
  sections,
  source,
  labelOverrides = {},
}: ReportDetailFieldRendererProps): ReactElement => {
  const { t, language } = useAppTranslation();

  return (
    <YStack gap="$true">
      {sections.map((section, sectionIndex) => {
        const dependencyValueMap = buildDependencyMapForSection(
          section,
          source,
          language,
        );

        return (
          <YStack key={section.title ?? sectionIndex} gap="$3">
            {section.title && (
              <SizableText size="$2" fontFamily="$bold" color="$color">
                {section.title}
              </SizableText>
            )}
            <YStack gap="$2">
              {section.fields.map((field) => {
                if (!isFieldVisible(field, dependencyValueMap)) {
                  return null;
                }

                const label = labelOverrides[field.id] ?? field.label ?? '';
                const rawValue = getReportDetailFieldValue(
                  field.id,
                  source,
                  language,
                );
                const value = formatReportDetailFieldValue({
                  field,
                  rawValue,
                  source,
                  language,
                  t,
                });

                return (
                  <KeyValueRow key={field.id} label={label} value={value} />
                );
              })}
            </YStack>
          </YStack>
        );
      })}
    </YStack>
  );
};
